import React, { Suspense, useCallback, useContext, useState } from 'react';
import { IUIUrval } from '../../../../Types/UrvalTypes';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import UrvalDialog from '../UrvalDialog/UrvalDialog';
import EDIconButton from '../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { CommonPropsAndTypes } from '../../../../BaseComponents/_Types/StandardComponentProps';
import RutinUrvalListQuery from '../SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import EDButton from '../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import { IDetaljurvalInfo } from '../HitIndicator/UrvalHitIndicator';
import EDFab from '../../../../BaseComponents/Fab/EDFab';
import EDKeybind from '../../../../BaseComponents/Keybinds/EDKeybind';
import EDTooltip from '../../../../BaseComponents/Tooltip/EDTooltip';
import { AvenyContext } from '../../../../Features/AvenyContext';
import { NodeUrvalQuery_node_TEDSelection_order } from '../SavedUrval/Queries/NodeUrvalQuery/_QueryTypes/NodeUrvalQuery';
import { IRenderButtonProps } from '../../../../Features/Delsystem/_Common/Const/Delsystem';

export interface IUrvalButtonProps {
  refetch: () => void;
  urval: IUIUrval | null;
  setUrval: ((newUrval: IUIUrval) => void) | null;
  collectionName: string;
  routineID: number | undefined;
  codeSystemID?: string;
  openFieldPickerWithAllExpandableOpen?: boolean;
  iconSize?: CommonPropsAndTypes.StandardSizesType;
  iconColor?: CommonPropsAndTypes.ExtendedColorType;
  captionButton?: boolean;
  fab?: boolean;
  /** Custom render function for the trigger button. When provided, takes priority over captionButton and fab. */
  renderButton?: (props: IRenderButtonProps) => React.ReactNode;
  detaljurvalInfo?: IDetaljurvalInfo;
  errorText?: string;
  errorDisable?: boolean;
  setSortInfoFromSelectionOrder?: (
    selectionOrder: NodeUrvalQuery_node_TEDSelection_order[]
  ) => void;
  expanded?: boolean;
  classes?: any;
}

const styles = (theme: Theme) => ({
  /** Styles applied to the bottom area of the dialog header  */
  dialogHeaderBottomArea: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  newPageButton: {
    color: 'inherit',
  },
  newPageButtonIcon: {
    marginRight: theme.spacing(1),
  },
});

/**
 * En standarddiseras icon-knapp för att öppna upp AvancedUrvalDialog komponenten. RutinUrval information hämtas till dialogen.
 */
export const UrvalButton: React.FC<
  React.PropsWithChildren<IUrvalButtonProps>
> = props => {
  const avenyContext = useContext(AvenyContext);
  const [open, setOpen] = useState(false);

  const setAsOpen = useCallback(() => {
    if (avenyContext.formHasChanged) {
      avenyContext.setOpenChoiceDialog(true, () => {
        setOpen(true);
      });
    } else {
      setOpen(true);
    }
  }, [avenyContext, setOpen]);

  const setAsClosed = useCallback(() => setOpen(false), [setOpen]);

  return (
    <RutinUrvalListQuery
      codeSystemID={props.codeSystemID}
      collectionName={props.collectionName}
      routineID={props.routineID}
      kodplanUrval={false}
    >
      {({ rutinUrvalList, variables, loading }) => (
        <>
          {props.renderButton ? (
            props.renderButton({
              onClick: () => setAsOpen(),
              disabled: props.errorDisable || loading,
              title: props.errorText,
              icon: 'filter_list',
              label: 'Urval',
            })
          ) : props.fab ? (
            <EDKeybind global alt keymap="v" onKeyDown={setAsOpen}>
              <EDTooltip title={props.errorText} placement="right">
                <span>
                  <EDFab
                    size="large"
                    onClick={setAsOpen}
                    color="primary"
                    margin={1.25}
                    disabledButtonStyle
                    disabled={loading}
                    testId="Fab-UrvalButton"
                    boxShadowNone
                  >
                    <EDIcon iconType="filter_list" size="large" color="white" />
                  </EDFab>
                </span>
              </EDTooltip>
            </EDKeybind>
          ) : props.captionButton ? (
            <EDButton
              label="Urval"
              color="inherit"
              icon={<EDIcon iconType="filter_list" color="inherit" />}
              onClick={setAsOpen}
              paddingY={0.5}
              labelNotUpperCase
              captionLabel
              flat
              disabled={props.errorDisable}
              disabledReason={props.errorText}
              testId="Button-UrvalButton"
            />
          ) : (
            <EDIconButton
              margin="none"
              icon="filter_list"
              iconSize={props.iconSize}
              iconColor={props.iconColor}
              tooltip="Urval"
              onClick={setAsOpen}
              disabled={props.errorDisable}
              disabledReason={props.errorText}
              testId="IconButton-UrvalButton"
            />
          )}
          <Suspense fallback={null}>
            {props.routineID && (
              <UrvalDialog
                setUrval={props.setUrval || (_ => {})}
                collectionName={props.collectionName}
                rutinUrvalList={rutinUrvalList}
                rutinUrvalVariables={variables}
                routineID={props.routineID}
                codeSystemID={props.codeSystemID}
                urval={props.urval}
                open={open}
                kodplanUrval={false}
                onClose={setAsClosed}
                openFieldPickerWithAllExpandableOpen={
                  props.openFieldPickerWithAllExpandableOpen
                }
                onCommit={() => {
                  setAsClosed();
                }}
                detaljurvalInfo={props.detaljurvalInfo}
                refetchCollectionQuery={props.refetch}
                setSortInfoFromSelectionOrder={
                  props.setSortInfoFromSelectionOrder
                }
              />
            )}
          </Suspense>
        </>
      )}
    </RutinUrvalListQuery>
  );
};

export default withStyles(UrvalButton, styles);
